import authRoutes from "@workspace/shared/core/services/auth/auth.routes";
import { api } from "@workspace/shared/core/services/api";
import { ILoginPayload, IRefreshTokenPayload, IAuth } from "@workspace/shared/core/types";
import axios from "axios";

/** Shown only if a 429 arrives with no wording — the server sends its own
 *  ("Too many sign-in attempts. Please wait a few minutes and try again."). */
const RATE_LIMITED_FALLBACK_MESSAGE =
  "Too many sign-in attempts. Please wait a few minutes and try again.";

/**
 * Signing in (and closing other sessions) is rate limited: a throttled
 * request comes back 429 with an explanation. It used to fall through to
 * "Invalid credentials" — so a coach who only had to WAIT was told the
 * password was wrong, and retried, extending the block (in-game parity).
 */
export const rateLimitMessage = (error: unknown): string | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return null;
  const message = error.response?.data?.message;
  return typeof message === "string" && message.trim() ? message : RATE_LIMITED_FALLBACK_MESSAGE;
};

const onLogin = async (payload: ILoginPayload) => {
  try {
    return await api.post<IAuth>(authRoutes.loginRoute, payload);
  } catch (e) {
    const throttled = rateLimitMessage(e);
    if (throttled) throw new Error(throttled);
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 403) {
        const isManySessions = e.response?.data?.message === "Too many active sessions";
        if (isManySessions) {
          const err = new Error(
            "You have exceed the amount of active sessions for this account please refer to your previous device or contact support team"
          ) as Error & { code: string };
          err.code = TOO_MANY_SESSIONS_ERROR_CODE;
          throw err;
        }
        throw new Error(e.response?.data?.message);
      }
      if (e.response?.status === 401) {
        const msg = e.response?.data?.message;
        if (msg === "Account not found") {
          throw new Error("Account not found");
        }
        if (msg === "Invalid password") {
          throw new Error("Invalid password");
        }
      }
    }
    throw new Error("Invalid credentials");
  }
};

const onRefreshToken = (payload: IRefreshTokenPayload) =>
  api.post(authRoutes.refreshTokenRoute, payload);

const onLogout = (payload: IRefreshTokenPayload) => api.post(authRoutes.logoutRoute, payload);

export const TOO_MANY_SESSIONS_ERROR_CODE = "TOO_MANY_SESSIONS";

const isTooManySessionsError = (error: unknown): error is Error & { code: string } => {
  return (
    error instanceof Error &&
    "code" in error &&
    (error as Error & { code: string }).code === TOO_MANY_SESSIONS_ERROR_CODE
  );
};

const onRevokeOtherSessions = async (payload: ILoginPayload) => {
  try {
    await api.post(authRoutes.revokeOtherSessionsRoute, payload);
  } catch (e) {
    const throttled = rateLimitMessage(e);
    if (throttled) throw new Error(throttled);
    throw e;
  }
};

/**
 * Register this device's session for one practice app (device-limit
 * enforcement — mirrors the in-game apps). The backend reads the app from
 * the `x-app-key` header, which our shared env pins to "Dashboard", so the
 * app's own AppSessionEnum code (e.g. "OL", "LB") is sent explicitly.
 * Idempotent per (device, user, app); 403 = device limit reached.
 */
const onRegisterSession = (appKey: string) =>
  api.post(authRoutes.registerSessionRoute, {}, { headers: { "x-app-key": appKey } });

/** Current sessions per app for this user (admin/session UIs). */
const onGetCurrentSessions = () => api.get(authRoutes.sessionsRoute);

/**
 * Release every session row held by THIS device (fingerprint) without
 * touching the auth tokens. POST /auth/logout under the "Dashboard" app key
 * (our env default) only revokes session_manager rows — it's what the
 * in-game dashboard calls on every mount so that returning to the dashboard
 * frees the apps you were in for other devices.
 */
const onReleaseDeviceSessions = () => api.post(authRoutes.logoutRoute, {});

export { isTooManySessionsError };
export default {
  onLogin,
  onRefreshToken,
  onLogout,
  onRevokeOtherSessions,
  onRegisterSession,
  onGetCurrentSessions,
  onReleaseDeviceSessions,
};
